import {
  SimpleKeyboard,
  KeyboardOptions,
  KeyboardLayoutObject,
  KeyboardReactInterface,
} from 'react-simple-keyboard';

import Language from 'types/LanguageEnum';

export enum KeyboardLayoutType {
  DEFAULT = 'default',
  ALTERNATE = 'alternate',
}

export type LayoutCandidates = Record<string, string>;

export type KeyboardLayout = {
  layout: KeyboardLayoutObject;
  layoutCandidates?: LayoutCandidates;
  name?: string
};

export type WritableInputTypesConfig = {
  excludedKeys?: KeyboardOptions['excludeFromLayout'];
  layout?: KeyboardLayoutObject;
  layoutName?: string;
  casing?: 'sentence';
};

// Candidate (IME) methods assigned to the main keyboard by the candidates module.
// All but onCandidatesChange are wired unconditionally in modules/candidates.ts, so they
// are required; onCandidatesChange is wired externally by JapaneseImeKeyboard.
export type CandidateMethods = {
  flushCandidates: () => void;
  refreshCandidates: () => void;
  setInputRange: (input: string) => void;
  updateCandidates: (button: string) => Promise<void>;
  selectNextCandidate: () => string | null;
  confirmSelectedCandidate: () => void;
  confirmFirstCandidate?: () => boolean
  onCandidatesChange?: (hasCandidates: boolean) => void;
};
export type CandidatesKeyboard = SimpleKeyboard & CandidateMethods;

// Shape of the mutable module object passed to `keyboard.registerModule(name, (module) => ...)`.
// react-simple-keyboard types this callback param as `any`; this is the minimal shape our
// modules actually populate (a saved reference to the original handlers, plus a layout index
// used by the multiKeyboards module).
export type KeyboardModule = {
  fn: {
    handleButtonClicked: SimpleKeyboard['handleButtonClicked'];
    clearInput?: SimpleKeyboard['clearInput'];
  };
  index?: number;
};

// react-simple-keyboard exposes our custom options only through its `[name: string]: any`
// index signature. This narrows the options we set/read so they stop being `any`.
// These modules only run on a live, configured keyboard, where `type` (the focused input's
// type), `language`, `layout` and `layoutName` are always set; `layoutType`, `zenkaku` and
// `keyboardName` are genuinely optional (`keyboardName` is set by the multiKeyboards module,
// so it is absent on single-layout input types like tel/number).
export type KeyboardOptionsExt = KeyboardOptions & {
  type: string;
  language: Language;
  layout: KeyboardLayoutObject;
  layoutName: string;
  layoutType?: KeyboardLayoutType;
  zenkaku?: boolean;
  keyboardName?: string;
};

// Props contract shared by every Custom keyboard component (Default, Japanese, QWERTY...).
export type KeyboardComponentProps = {
  type: string;
  language: Language;
  layoutType: KeyboardLayoutType;
  layoutCandidates: LayoutCandidates;
  keyboardRef: (ref: KeyboardReactInterface) => void;
  onChange: (value: string) => void;
  onInit: (keyboard: KeyboardReactInterface) => void;
  onKeyReleased: (key: string) => void;
  onSelectionChange: (selection: [number, number]) => void;
};
